import React, { useState } from "react";
import { Table, Button, message, Typography, Tooltip } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import ViewApplicantModal from "./ApplicantModal";
import { useDownloadBulkCvs } from "../hooks/useJobs";

const { Title } = Typography;

const ApplicantsTable = ({ jobData, appDataloading, pagination }) => {
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [appModal, setAppModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    downloadBulk,
    loading: bulkDownloading,
    error,
  } = useDownloadBulkCvs();

  const handleViewApplication = (id) => {
    setAppModal(true);
    setSelectedApplicant(id);
  };

  const handleCloseApplication = () => {
    setAppModal(false);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "user_name",
      key: "user_name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
      responsive: ["sm"],
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
      responsive: ["sm"],
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      ellipsis: true,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewApplication(record.id)}
          style={{
            color: "#1890ff",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, selectedRows) => {
      console.log("Selection changed:", newSelectedRowKeys, selectedRows);
      setSelectedRowKeys(newSelectedRowKeys);
    },
    onSelect: (record, selected, selectedRows, nativeEvent) => {
      console.log("Individual select:", record, selected);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      console.log("Select all:", selected, selectedRows, changeRows);
    },
    getCheckboxProps: (record) => ({
      // Ensure each row has a unique identifier
      name: record.id?.toString() || record.key?.toString(),
    }),
    columnTitle: (originNode) => (
      <Tooltip title="Select all applicants" placement="top">
        {originNode}
      </Tooltip>
    ),
    renderCell: (checked, record, index, originNode) => {
      return (
        <Tooltip title={`Select applicant ${index + 1}`} placement="top">
          {originNode}
        </Tooltip>
      );
    },
  };

  // Download file from blob response
  const downloadZipFile = (blob, filename = "applicant-cvs.zip") => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      message.error("Failed to download file");
    }
  };

  const handleBulkDownload = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select at least one applicant to download CVs.");
      return;
    }

    setLoading(true);

    try {
      // Create comma-separated list of selected IDs
      const idsQuery = selectedRowKeys.join(",");
      console.log("Sending IDs:", idsQuery); // Debug log

      // Call the API to generate and download zip file
      const response = await downloadBulk(idsQuery);

      // Handle different response types
      if (response instanceof Blob || response.data instanceof Blob) {
        // If response is already a blob
        const blob = response instanceof Blob ? response : response.data;
        downloadZipFile(blob, `applicant-cvs-${Date.now()}.zip`);
      } else if (response.url || response.data?.url) {
        // If response contains a download URL
        const downloadUrl = response.url || response.data.url;
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `applicant-cvs-${Date.now()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Handle other response formats
        console.warn("Unexpected response format:", response);
        message.error("Unexpected response format from server");
        return;
      }

      message.success(
        `Successfully downloaded CVs for ${selectedRowKeys.length} applicant(s)`
      );
      setSelectedRowKeys([]); // Clear selection after successful download
    } catch (error) {
      console.error("Bulk download error:", error);

      // Handle different error types
      if (error.response?.status === 404) {
        message.error("Some CVs could not be found. Please try again.");
      } else if (error.response?.status === 500) {
        message.error("Server error occurred. Please try again later.");
      } else if (error.message?.includes("Network")) {
        message.error(
          "Network error. Please check your connection and try again."
        );
      } else {
        message.error("Failed to download CVs. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="applicants-table-container">
      {/* Header */}
      <div className="applicants-table-header">
        <Title level={4} style={{ margin: 0, color: "#08839b" }}>
          Applicants
        </Title>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleBulkDownload}
          loading={loading || bulkDownloading}
          disabled={selectedRowKeys.length === 0}
          style={{
            backgroundColor: "#08839b",
            borderColor: "#08839b",
            borderRadius: "6px",
            fontWeight: 500,
          }}
        >
          Download Selected ({selectedRowKeys.length})
        </Button>
      </div>

      {/* Selection Info */}
      {selectedRowKeys.length > 0 && (
        <div
          style={{
            marginBottom: "16px",
            padding: "8px 12px",
            backgroundColor: "#e6f7ff",
            borderRadius: "4px",
            border: "1px solid #91d5ff",
          }}
        >
          <span style={{ color: "#08839b", fontWeight: 500 }}>
            {selectedRowKeys.length} applicant(s) selected for download
          </span>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div
          style={{
            marginBottom: "16px",
            padding: "8px 12px",
            backgroundColor: "#fff2f0",
            borderRadius: "4px",
            border: "1px solid #ffccc7",
          }}
        >
          <span style={{ color: "#cf1322", fontWeight: 500 }}>
            Error: {error.message || "Failed to download CVs"}
          </span>
        </div>
      )}

      {/* Table */}
      <Table
        rowSelection={rowSelection}
        columns={columns}
        loading={appDataloading}
        dataSource={jobData}
        pagination={pagination}
        scroll={{ x: 800 }}
        className="custom-table"
        rowKey={(record) => record.id || record.key} // Ensure proper row key
        rowClassName={(record, index) =>
          index % 2 === 1 ? "ant-table-row-striped" : ""
        }
      />

      <ViewApplicantModal
        visible={appModal}
        onClose={handleCloseApplication}
        selectedApplicant={selectedApplicant}
      />
    </div>
  );
};

export default ApplicantsTable;
